export const TEAM_SITEGLUE_HREF = "/team-siteglue";
export const HIRE_TRISH_HREF = "/hire-trish";
export const SERVICES_HREF = "/services";
export const SERVICES_SEO_HREF = "/services/seo";
export const TEAM_SITEGLUE_DIRECTORY_TITLE = "Meet Your AI Marketing Team";
export const HIRE_TRISH_CTA_LABEL = "Hire Trish — Free Marketing Analysis";
export const MEET_TEAM_SITEGLUE_CTA = "Meet Team SiteGlue";

export const TEAM_SITEGLUE_SPECIALIST_IDS = [
  "trish",
  "sam",
  "samantha",
  "ben",
  "tony",
  "rob",
  "emily",
] as const;

export type TeamSiteGlueSpecialistId = (typeof TEAM_SITEGLUE_SPECIALIST_IDS)[number];

export type TeamSiteGlueSpecialist = {
  id: TeamSiteGlueSpecialistId;
  name: string;
  title: string;
  intro: string;
  longIntro: string;
  capabilities: readonly string[];
  outcomes: readonly string[];
  portraitSrc: string;
  portraitFallbackSrc?: string;
  meetHref: string;
  howICanHelpHref: string;
  howICanHelpLabel: string;
  featured?: boolean;
  comingSoon?: boolean;
};

const PORTRAIT = (id: TeamSiteGlueSpecialistId) => `/marketing/team-siteglue/${id}.jpg`;

export const TEAM_SITEGLUE_ROSTER: readonly TeamSiteGlueSpecialist[] = [
  {
    id: "trish",
    name: "Trish",
    title: "AI Marketing Director",
    featured: true,
    intro:
      "Trish analyzes your business, recommends the right specialists, and coordinates a team that improves visibility, engagement, and conversion.",
    longIntro:
      "Meet Trish, your AI Marketing Director. She reviews your website and business, coordinates supported visibility and conversion checks, and recommends specialists based on what she finds—not a default roster of everyone. She is the primary point of contact for Team SiteGlue.",
    capabilities: [
      "Analyze your website and business profile",
      "Coordinate checks for search, AI answers, agent readiness, and conversion",
      "Recommend specialists with supporting evidence",
      "Explain expected outcomes in plain language",
      "Coordinate onboarding once you approve a team",
    ],
    outcomes: [
      "A clear view of where customers and AI agents get stuck",
      "A recommended team matched to actual findings",
      "A path from free analysis to activation without paying first",
    ],
    portraitSrc: PORTRAIT("trish"),
    portraitFallbackSrc: "/marketing/trish-onboarding-agent.png",
    meetHref: `${TEAM_SITEGLUE_HREF}/trish`,
    howICanHelpHref: HIRE_TRISH_HREF,
    howICanHelpLabel: "Free Marketing Analysis",
  },
  {
    id: "sam",
    name: "Sam",
    title: "AEO Specialist",
    intro:
      "Sam helps your business show up in AI-generated answers and recommendations—without inventing rankings or citations.",
    longIntro:
      "Sam focuses on answer-engine optimization: whether AI systems can understand who you are, what you offer, and when they can cite you. Live visibility tests are labeled when they are verified. Gaps are recommendations, not guaranteed placements.",
    capabilities: [
      "Review how clearly your business entity is described",
      "Find citation and content gaps AI systems can use",
      "Run a free answer-engine audit on your website",
      "Coordinate website copy updates with Tony when you approve them",
    ],
    outcomes: [
      "Clearer answers about your business in AI tools",
      "Honest reporting of what was verified versus estimated",
    ],
    portraitSrc: PORTRAIT("sam"),
    meetHref: `${TEAM_SITEGLUE_HREF}/sam`,
    howICanHelpHref: "/audits/answer-engine-optimization",
    howICanHelpLabel: "AEO Audit",
  },
  {
    id: "samantha",
    name: "Samantha",
    title: "SEO Specialist",
    comingSoon: true,
    intro:
      "Samantha will evaluate traditional search: crawlability, titles, headings, and local signals. Live ranking reports are not available yet.",
    longIntro:
      "Samantha’s role is traditional search optimization—how search engines crawl, index, and understand your pages. Team SiteGlue does not currently run a live keyword-ranking product. This page explains what she will cover. Do not expect verified traffic or ranking claims today.",
    capabilities: [
      "Review crawlability and indexing signals (planned)",
      "Evaluate titles, descriptions, headings, and internal links (planned)",
      "Identify local search opportunities (planned)",
      "Recommend page improvements for Tony to implement after approval",
    ],
    outcomes: [
      "A clearer search foundation once SEO analysis ships",
      "No invented keyword rankings or traffic numbers",
    ],
    portraitSrc: PORTRAIT("samantha"),
    meetHref: `${TEAM_SITEGLUE_HREF}/samantha`,
    howICanHelpHref: SERVICES_SEO_HREF,
    howICanHelpLabel: "SEO Analysis",
  },
  {
    id: "ben",
    name: "Ben",
    title: "CRO Specialist",
    intro:
      "Ben looks at first impressions, calls to action, trust, and mobile usability so more visits become inquiries.",
    longIntro:
      "Ben evaluates conversion: whether visitors understand the offer, see a next step, and can complete it on a phone. He coordinates website changes with Tony and engagement improvements with Emily after you approve them.",
    capabilities: [
      "Review headlines, offers, and calls to action",
      "Check trust signals and mobile usability",
      "Spot gaps in lead capture and booking paths",
      "Run a free conversion audit on your website",
    ],
    outcomes: [
      "Clearer next steps for website visitors",
      "Fewer dead ends between interest and contact",
    ],
    portraitSrc: PORTRAIT("ben"),
    meetHref: `${TEAM_SITEGLUE_HREF}/ben`,
    howICanHelpHref: "/audits/conversion-optimization",
    howICanHelpLabel: "CRO Audit",
  },
  {
    id: "tony",
    name: "Tony",
    title: "AI Webmaster",
    intro:
      "Tony implements approved website changes through the SiteGlue WordPress plugin—never by asking for your admin password in chat.",
    longIntro:
      "Tony is the implementation specialist. He updates content, metadata, discovery files, and supported widgets after you authorize the work. Changes are previewed, logged, and verified. He does not claim a publish succeeded without a check.",
    capabilities: [
      "Implement approved visibility and conversion recommendations",
      "Update WordPress content, metadata, and discovery files",
      "Configure supported SiteGlue widgets",
      "Keep an implementation log and support rollback where possible",
    ],
    outcomes: [
      "Website changes that match the audit, not a guess",
      "Human authorization before anything goes live",
    ],
    portraitSrc: PORTRAIT("tony"),
    meetHref: `${TEAM_SITEGLUE_HREF}/tony`,
    howICanHelpHref: "/resources/wordpress-plugin",
    howICanHelpLabel: "WordPress Optimization",
  },
  {
    id: "rob",
    name: "Rob",
    title: "AI Agent Engagement Specialist",
    intro:
      "Rob makes it easier for personal AI agents to find your business, get accurate answers, and take authorized actions.",
    longIntro:
      "Rob owns agent readiness and agent-to-agent engagement as one role. He evaluates whether external AI agents can discover your business, use your knowledge, and call supported tools. Different personal agents (Grok, Claude, and others) do not all support the same protocols.",
    capabilities: [
      "Evaluate how AI agents discover and use your site",
      "Support authorized actions through connected tools",
      "Answer supported agent-to-agent inquiries from shared knowledge",
      "Escalate requests that need a person",
    ],
    outcomes: [
      "A site that personal AI agents can actually use",
      "Clearer handoff when a human still needs to help",
    ],
    portraitSrc: PORTRAIT("rob"),
    meetHref: `${TEAM_SITEGLUE_HREF}/rob`,
    howICanHelpHref: "/audits/agent-readiness",
    howICanHelpLabel: "ARO Audit",
  },
  {
    id: "emily",
    name: "Emily",
    title: "Human Engagement Specialist",
    intro:
      "Emily is the SiteGlue Agent customers already talk to—chat, phone, text, and email—with people always able to take over.",
    longIntro:
      "Emily is not a new parallel product. She is the existing SiteGlue engagement agent across enabled channels: website chat, phone, text, email, and browser or video calls where you turn them on. Channel settings, routing, and training stay in your current dashboard.",
    capabilities: [
      "Answer questions on enabled communication channels",
      "Capture and qualify inquiries",
      "Help with appointment requests when scheduling is connected",
      "Connect people to your human team with context",
    ],
    outcomes: [
      "Fewer missed inquiries after hours",
      "Conversations your staff can continue",
    ],
    portraitSrc: PORTRAIT("emily"),
    meetHref: `${TEAM_SITEGLUE_HREF}/emily`,
    howICanHelpHref: "/product",
    howICanHelpLabel: "Human Engagement Assessment",
  },
] as const;

export const TEAM_SITEGLUE_DIRECTOR = TEAM_SITEGLUE_ROSTER[0]!;

export function getTeamSiteGlueSpecialist(
  id: string,
): TeamSiteGlueSpecialist | undefined {
  return TEAM_SITEGLUE_ROSTER.find((member) => member.id === id);
}

export function teamSiteGlueSpecialists(): readonly TeamSiteGlueSpecialist[] {
  return TEAM_SITEGLUE_ROSTER.filter((member) => !member.featured);
}

export function meetSpecialistLabel(name: string): string {
  return `Meet ${name}`;
}

export function howICanHelpLabel(member: TeamSiteGlueSpecialist): string {
  return "How I Can Help";
}

export const TEAM_SITEGLUE_SERVICES_NAV = [
  {
    label: "ARO Audit",
    href: "/audits/agent-readiness",
    description: "See how personal AI agents discover and use your business.",
  },
  {
    label: "AEO Audit",
    href: "/audits/answer-engine-optimization",
    description: "See whether AI systems can understand and cite your business.",
  },
  {
    label: "CRO Audit",
    href: "/audits/conversion-optimization",
    description: "See whether visitors can take the next step on your site.",
  },
  {
    label: "SEO Analysis",
    href: SERVICES_SEO_HREF,
    description: "Traditional search evaluation — not a live ranking product yet.",
    comingSoon: true,
  },
  {
    label: "WordPress Optimization",
    href: "/resources/wordpress-plugin",
    description: "Tony’s implementation path for approved website changes.",
  },
  {
    label: "SiteGlue Agent",
    href: "/product",
    description: "Emily’s human engagement across chat, phone, text, and email.",
  },
] as const;

export const HIRE_TRISH_SCAN_REPORTS = ["aro", "aeo", "cro"] as const;

export const TEAM_SITEGLUE_HOW_IT_WORKS_STEPS = [
  { title: "Hire Trish", body: "Start a free marketing analysis with your website URL. No account required." },
  { title: "Analyze business", body: "Trish coordinates supported checks for agent readiness, AI answers, and conversion." },
  { title: "Review findings", body: "You see verified results, estimates, and checks that were not available." },
  { title: "Recommended team", body: "Specialists are suggested from findings—not automatically the full roster." },
  { title: "Review proposal", body: "Add or remove specialists and review pricing before anything is activated." },
  { title: "Activate specialists", body: "Approved teammates use your existing SiteGlue Agent platform and knowledge." },
  { title: "Implement improvements", body: "Tony applies authorized website changes. Emily and Rob handle engagement." },
  { title: "Measure results", body: "Trish reports what changed using evidence, not a success toast." },
] as const;
